import { forwardRef, ClipboardEvent, useState, useCallback } from "react";
import styles from "./AppInput.module.scss";
import PasswordGeneratorPopup from "@/components/modals/PasswordGeneratorPopup/PasswordGeneratorPopup";

type AppInputIconProps = {
  fill?: string;
  visible?: boolean;
};

interface AppInputProps {
  label?: string;
  placeholder?: string;
  value: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onFocus?: () => void;
  type?: string;
  IconBefore?: React.ReactNode;
  IconAfter?: React.ReactNode;
  required?: boolean;
  name: string;
  maxLength?: number;
  error?: string | boolean;
  className?: string;
  onPaste?: (e: ClipboardEvent<HTMLInputElement>) => void;
  withoutErrors?: boolean;
  hasfullWidth?: boolean;
  labelClass?: string;
  iconClick?: () => void;
  disabled?: boolean;
  newPassword?: boolean;
  onGeneratedPassword?: (password: string) => void;
}

const AppInput = forwardRef<HTMLInputElement, AppInputProps>(
  (
    {
      label,
      placeholder,
      value,
      type,
      onChange,
      onBlur,
      onKeyDown,
      onFocus,
      IconBefore,
      IconAfter,
      required,
      name,
      maxLength,
      error,
      className,
      onPaste,
      withoutErrors = false,
      hasfullWidth = false,
      labelClass,
      iconClick,
      disabled,
      newPassword,
      onGeneratedPassword,
    },
    ref
  ) => {
    const [popupOpen, setPopupOpen] = useState(false);

    const handleUsePassword = useCallback((password: string) => {
      onGeneratedPassword?.(password);
    }, [onGeneratedPassword]);

    return (
      <div className={`${styles.container} ${hasfullWidth ? "w-full" : ""}`}>
        {label && (
          <label className={`${styles.label} ${labelClass}`}>
            {label} {required && <span className={styles.required}>*</span>}
          </label>
        )}
        <div
          className={`${styles.inputContainer} ${className} border border-gray-300`}
        >
          {IconBefore && (
            <span className={styles.iconSpan}>
             {IconBefore}
            </span>
          )}
          <input
            className={`${styles.input} ${labelClass ? "!p-[10px] " : ""}`}
            value={value}
            name={name}
            onChange={onChange}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
            onFocus={onFocus}
            onPaste={onPaste}
            type={type ?? "text"}
            placeholder={placeholder}
            maxLength={maxLength}
            ref={ref}
            tabIndex={0}
            disabled={disabled}
            autoComplete="off"
          />
          {IconAfter && (
            <button
              type="button"
              className={`${styles.myIcon} ps-1 me-2 h-full flex items-center size-8  `}
              style={{ cursor: "pointer" }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                iconClick?.();
              }}
            >
              {IconAfter}
            </button>
          )}
        </div>
        {!withoutErrors && error && (
          <p className={styles.errorMessage}>{error}</p>
        )}
        {newPassword && (
          <button
            type="button"
            onClick={() => setPopupOpen(true)}
            className="self-start text-xs text-[#2252d7] hover:underline flex items-center gap-1 mt-0.5"
          >
            ✦ Generate password
          </button>
        )}
        {popupOpen && (
          <PasswordGeneratorPopup
            onUse={handleUsePassword}
            onClose={() => setPopupOpen(false)}
          />
        )}
      </div>
    );
  }
);

export default AppInput;
